<script setup>
import { computed, ref, onMounted } from "vue";
import VuePictureCropper, { cropper } from "vue-picture-cropper";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useModals } from "@/utils/useModals";
import BaseLoader from "@/components/blocks/BaseLoader.vue";

const storeField = useFieldsStore();
const { openModal } = useModals();

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const isShowModal = ref(false);
const uploadInput = ref(null);
const pic = ref("");
const photoPreview = ref("");
const isLoader = ref(false);

const props = defineProps({
	userApplication: {
		type: Object,
	},
	cropWidth: {
		type: Number,
		default: 333,
	},
	cropHeight: {
		type: Number,
		default: 200,
	},
	isCookPhoto: {
		type: Boolean,
		default: false
	},
	noCrop: { 
		type: Boolean,
		default: false
	}
})

const aspectRatio = computed(() => props.cropWidth / props.cropHeight);
const titleAddPhoto = computed(() => props.isCookPhoto ? "Загрузить личное фото" : "Загрузить фото в интерьере заведения");
const iconAddPhoto = computed(() => props.isCookPhoto ? "#user_select_icon" : "#picture_icon");

const emit = defineEmits(["update:photo"]);

const onFileSelected = (file) => {
  	emit("update:photo", file); 
};
function selectFile(e) {
	isLoader.value = true;
	const files = e.target.files;
	if (!files || !files.length) return;

	const file = files[0];

	if (props.noCrop) {
		processFileWithoutCrop(file);
		return;
	}

	const reader = new FileReader();
	reader.readAsDataURL(file);
	reader.onload = () => {
		pic.value = String(reader.result);
		isShowModal.value = true;
		isLoader.value = false;
		if (uploadInput.value) uploadInput.value.value = "";
	};
	
}


function processFileWithoutCrop(file) {
	if (file.size > MAX_FILE_SIZE) {
		openModal("photoSizeError", { text: "Размер файла более 5 МБ" });
		isLoader.value = false;
		return;
	}
	
	props.userApplication.photo = file;
	onFileSelected(file);
	
	// Создаем превью
	const reader = new FileReader();
	reader.readAsDataURL(file);
	reader.onload = () => {
		photoPreview.value = String(reader.result);
		props.userApplication.isPicAdd = true;
		isLoader.value = false;
	};
}

async function getResult() {
	
	if (!cropper) return;

	const blob = await cropper.getBlob();
	if (!blob) return;

	// Создаем File из Blob
	const file = new File([blob], "user-photo.jpg", { type: blob.type });

	if (file.size <= MAX_FILE_SIZE) {
		props.userApplication.photo = file;
		onFileSelected(file);

		// Для превью сохраняем также base64
		const base64 = cropper.getDataURL();
		photoPreview.value = base64;
		isShowModal.value = false;
		props.userApplication.isPicAdd = true;
	} else {
		isShowModal.value = false;
		openModal("photoSizeError", { text: "Размер файла более 5 МБ" });
	}
	isLoader.value = false;
}

function resetPreview() {
	props.userApplication.photo = "";
	photoPreview.value = "";
	props.userApplication.isPicAdd = false;
	onFileSelected(props.userApplication.photo);
}

onMounted(() => {});
</script>

<template>
	<div class="user-crooper">
		<!-- Валидация на заполненость -->
		<input type="text" style="display: none" v-model="props.userApplication.photo">
		<!-- Выбор изображения для обрезки -->
		<div :class="['user-image', { active: props.userApplication.isPicAdd }]">
			<label class="user-image__btn-select" v-if="!photoPreview">
				<input ref="uploadInput" type="file" class="user-image__real" accept="image/jpg, image/jpeg, image/png, image/gif" @change="selectFile" />
				<svg class="user-image__icon" width="136" height="136"><use :href="iconAddPhoto"></use></svg>
				<span>{{ titleAddPhoto }}</span>
			</label>
			<div class="user-image__holder-select-image" v-else>
				<label class="user-image__btn-select">
					<input ref="uploadInput" type="file" class="user-image__real" accept="image/jpg, image/jpeg, image/png, image/gif" @change="selectFile" />
					<img class="user-image__img" width="300" height="300" :src="photoPreview" alt="изображение" />
					<span class="user-image__btn-edit">
						<svg width="20" height="20"><use href="#edit"></use></svg>
					</span>
					<button @click="resetPreview" class="user-image__btn-close">
						<svg width="20" height="20"><use href="#trash"></use></svg>
					</button>
				</label>
			</div>
			<div class="user-image__footer" v-if="!props.userApplication.isPicAdd">
				<span>Доступные форматы</span>
				<span>JPEG/JPG, PNG, WEBP, HEIC/HEIF, AVIF</span>
				<span>Размер файла не более 5 МБ</span>
			</div>
		</div>
		<BaseLoader v-if="isLoader" />
		<!-- Модальное окно обрезки -->
		<div class="modal-wrap" v-if="isShowModal && !props.noCrop">
			<div class="modal-mask"></div>
			<div class="modal-scroll-view">
				<div class="modal">
					<div class="modal-title">
						<span class="title">Отрегулируйте фото по области</span>
					</div>

					<div class="modal-content">
						<VuePictureCropper
							:boxStyle="{
								width: '100%',
								height: '100%',
								backgroundColor: '#f8f8f8',
								margin: 'auto',
							}"
							:img="pic"
							:options="{
								viewMode: 1,
								dragMode: 'move',
								aspectRatio: aspectRatio,
								cropBoxResizable: true,
								autoCropArea: 1,
								minCropBoxWidth: props.cropWidth,
								minCropBoxHeight: props.cropHeight,
							}"
							
						/>
					</div>
					<div class="modal-btns">
						<button class="modal-btn-close" @click="isShowModal = false">
							<svg width="20" height="20"><use href="#x"></use></svg>
						</button>
						<label class="btn mobile-full --small --transparent-red">
							<span>Поменять фото</span>
							<input ref="uploadInput" type="file" style="display: none" accept="image/jpg, image/jpeg, image/png, image/gif" @change="selectFile" />
						</label>
						<button class="btn mobile-full --small" @click="getResult">Применить</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.user-crooper {
	height: 100%;
	aspect-ratio: 1;
}
.user-image {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4.5rem;
	border: 1px dashed hsla(351, 78%, 38%, 1);
	padding: 4rem 1rem 2rem;
	@media (max-width: vars.$x480) {
		gap: 2.7rem;
	}
	&.active {
		padding: 2rem;
		width: 100%;
		height: 100%;
		.user-image__holder-select-image {
			width: 100%;
			height: 100%;
		}
		.user-image__img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}
}
.user-image__holder-select-image {
	position: relative;
}
.user-image__btn-select {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1.5rem;
	color: hsla(351, 78%, 38%, 1);
	height: 100%;
	span {
		font-weight: 400;
		font-size: 1.6rem;
		line-height: 120%;
		text-align: center;
	}
}
.user-image__real {
	width: 0;
	height: 0;
	position: absolute;
	opacity: 0;
	z-index: -1;
}
.user-image__icon {
	fill: transparent;
}
.user-image__img {
	width: 100%;
	
}
.user-image__btn-edit {
	position: absolute;
	display: grid;
	place-items: center;
	width: 4rem;
	height: 4rem;
	background-color: var(--white);
	border-radius: 0.5rem;
	right: 5.5rem;
	top: 0.8rem;
	padding: 0.7rem;
	color: var(--black);
	transition:  var(--transition) ease-in;
	svg {
		flex-shrink: 0;
		width: 2rem;
		height: 2rem;
	}
	@media (hover: hover){
		&:hover {
			background-color: var(--red);
			color: var(--white);
			}
	}
}
.user-image__btn-close {
	display: grid;
	place-items: center;
	width: 4rem;
	height: 4rem;
	background-color: var(--red);
	border-radius: 0.5rem;
	position: absolute;
	right: 1.1rem;
	top: 0.8rem;
	padding: 0.7rem;
	cursor: pointer;
	color: var(--white);
	svg {
		flex-shrink: 0;
		width: 2rem;
		height: 2rem;
	}
	@media (hover: hover){
		&:hover {
			background-color: var(--white);
			color: var(--black);
		}
	}
}
.user-image__footer {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	text-align: center;
}

.modal-wrap {
	--modal-padding: 44px;
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	overflow: hidden;
	z-index: 99;
}
.modal-mask {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(101, 108, 133, 0.8);
}
.modal-scroll-view {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	overflow: auto;
}
.modal {
	position: relative;
	top: 100px;
	width: 90%;
	max-width: 600px;
	background-color: #fff;
	border-radius: 4px;
	margin: 0 auto 200px;
	box-sizing: border-box;
	padding: 44px;
}
.modal-content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
}
.modal-title {
	margin-bottom: 34px;
}
.title {
	font-weight: 400;
	font-style: Regular;
	font-size: 22px;
	line-height: 120%;
	letter-spacing: 0%;
}
.modal-btns {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	margin-top: 30px;
	@media (max-width: vars.$x480) {
		flex-direction: column;
		width: 100%;
	}
}
.cropper-view-box {
	border: 0px solid transparent !important;
}

.modal-btn-close {
	position: absolute;
	top: 16px;
	right: 16px;
	display: grid;
	place-items: center;
	svg {
		stroke: #000;
		fill: transparent;
	}
}
</style>
